import { Circle, Group, Polygon, Polyline, Rect, Textbox } from 'fabric';
import type { FabricObject } from 'fabric';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type {
  ContentSourceSpec,
  FillValue,
  StandardContentPayload,
  ToolRecipe,
} from '../types';

/**
 * "I Can" statements — a learning-target poster: a titled banner over a card of
 * student-voice objectives, each with a star / check / dot bullet. A classroom
 * ops staple (posted learning targets). Excellent fully manual and Smart-
 * fillable: the graph turns a standard's description + skills into "I can …"
 * lines.
 *
 * Stock primitives only (Rect / Circle / Polygon / Polyline / Textbox). Bullets
 * are DRAWN (no emoji-font dependency). Params arrive untrusted; generate()
 * coerces every read and never throws.
 */

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}
function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}
function jsonArray<T>(v: unknown, fallback: T[]): T[] {
  let parsed: unknown = v;
  if (typeof v === 'string') {
    try {
      parsed = JSON.parse(v);
    } catch {
      return fallback;
    }
  }
  return Array.isArray(parsed) ? (parsed as T[]) : fallback;
}
function solidFromFill(fill: unknown, fallback: string): string {
  if (typeof fill === 'string') return fill.trim() || fallback;
  if (fill && typeof fill === 'object') {
    const f = fill as { type?: string; color?: string; from?: string; stops?: { color?: string }[] };
    if (f.type === 'solid' && typeof f.color === 'string') return f.color;
    if (f.type === 'linear-gradient' || f.type === 'radial-gradient') {
      if (Array.isArray(f.stops) && f.stops.length && f.stops[0].color) return f.stops[0].color;
      if (typeof f.from === 'string') return f.from;
    }
  }
  return fallback;
}
function readableInkOn(bg: string, ink: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(bg).trim());
  if (!m) return ink;
  const n = parseInt(m[1], 16);
  const L = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return L > 0.6 ? ink : '#ffffff';
}
/** First sentence of a description, punctuation stripped. */
function firstSentence(s: string): string {
  const t = s.trim();
  const cut = t.search(/[.!?]/);
  return (cut >= 0 ? t.slice(0, cut) : t).trim();
}
/** Lowercase only the first character (so "Add …" → "add …"). */
function lowerFirst(s: string): string {
  return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

const INK = '#0f172a';
const DEFAULT_FONT = 'Nunito';
const SECTION_CONTENT = 'Content';
const SECTION_STYLE = 'Style';
const SECTION_COLORS = 'Colors';
const SECTION_FONTS = 'Fonts';

const MAX_STATEMENTS = 8;

const SAMPLE_STATEMENTS = [
  'I can explain what the numerator and denominator of a fraction mean.',
  'I can compare two fractions using a benchmark like 1/2.',
  'I can find equivalent fractions by multiplying or dividing.',
  'I can add and subtract fractions with the same denominator.',
];

// ---------------------------------------------------------------------------
// Drawn bullets — pure shapes, no emoji font.
// ---------------------------------------------------------------------------

function bulletShapes(style: string, cx: number, cy: number, size: number, color: string): FabricObject[] {
  if (style === 'star') {
    const outer = size / 2;
    const inner = outer * 0.42;
    const verts: { x: number; y: number }[] = [];
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? outer : inner;
      const a = -Math.PI / 2 + (i * Math.PI) / 5;
      verts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
    }
    return [new Polygon(verts, { fill: color, stroke: color, strokeWidth: 1, strokeLineJoin: 'round' })];
  }
  if (style === 'dot') {
    return [new Circle({ left: cx, top: cy, originX: 'center', originY: 'center', radius: size * 0.28, fill: color })];
  }
  // check (default) — a checkbox with a tick.
  const h = size / 2;
  const box = new Rect({
    left: cx,
    top: cy,
    originX: 'center',
    originY: 'center',
    width: size,
    height: size,
    rx: 6,
    ry: 6,
    fill: '',
    stroke: color,
    strokeWidth: 3,
  });
  const tick = new Polyline(
    [
      { x: cx - size * 0.26, y: cy + size * 0.02 },
      { x: cx - size * 0.06, y: cy + size * 0.22 },
      { x: cx + size * 0.3, y: cy - size * 0.22 },
    ],
    { fill: '', stroke: color, strokeWidth: 4, strokeLineCap: 'round', strokeLineJoin: 'round' },
  );
  return [box, tick];
}

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  wants: ['skills'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    const next = { ...current };
    const std = payload?.standard;
    const out: string[] = [];
    const desc = firstSentence(rawStr(std?.description, ''));
    if (desc) out.push(`I can ${lowerFirst(desc)}`);
    const skills = Array.isArray(payload?.skills) ? payload.skills : [];
    for (const s of skills) {
      const nm = rawStr(s?.name, '').trim();
      if (nm) out.push(`I can ${lowerFirst(nm)}`);
    }
    // Only overwrite when we actually derived statements.
    if (out.length) next.statements = out.slice(0, MAX_STATEMENTS);
    if (std?.code) next.title = `I Can — ${std.code}`;
    if (std?.code) next.sourceStandardCode = std.code;
    if (std?.id) next.sourceStandardId = std.id;
    return next;
  },
};

const iCanStatements: ToolRecipe = {
  id: 'i-can-statements',
  domain: 'ops',
  category: 'decor',
  tags: ['learning-target', 'i-can', 'objectives', 'standards', 'checklist', 'goals'],
  name: 'I Can Statements',
  version: 1,
  description:
    'An "I Can" learning-target poster — a titled list of student-voice objectives with star, check, or dot bullets. Type your own or generate them from a standard.',
  defaults: {
    title: 'I Can…',
    statements: [...SAMPLE_STATEMENTS],
    bulletStyle: 'check',
    bannerFill: '#2563eb',
    cardFill: '#ffffff',
    textColor: INK,
    textFont: DEFAULT_FONT,
    titleFont: '',
    statementFont: '',
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    { key: 'title', label: 'Title', type: 'text', section: SECTION_CONTENT },
    {
      key: 'statements',
      label: 'Statements',
      type: 'list',
      itemType: 'text',
      minItems: 1,
      maxItems: MAX_STATEMENTS,
      section: SECTION_CONTENT,
      help: 'One "I can…" objective per line.',
    },
    {
      key: 'bulletStyle',
      label: 'Bullet style',
      type: 'select',
      section: SECTION_STYLE,
      options: [
        { value: 'star', label: 'Star' },
        { value: 'check', label: 'Checkbox' },
        { value: 'dot', label: 'Dot' },
      ],
    },
    { key: 'bannerFill', label: 'Banner fill', type: 'fill', section: SECTION_COLORS, help: 'Solid, gradient, or texture.' },
    { key: 'cardFill', label: 'Card fill', type: 'fill', section: SECTION_COLORS, help: 'Solid, gradient, or texture.' },
    { key: 'textColor', label: 'Text color', type: 'color', section: SECTION_COLORS },
    { key: 'textFont', label: 'Text font (default for all parts)', type: 'font', section: SECTION_FONTS },
    { key: 'titleFont', label: 'Title font', type: 'font', section: SECTION_FONTS },
    { key: 'statementFont', label: 'Statement font', type: 'font', section: SECTION_FONTS },
    { key: 'sourceStandardCode', label: 'Source standard code', type: 'text', section: SECTION_CONTENT, advanced: true, help: 'Set automatically when filled from a standard.' },
    { key: 'sourceStandardId', label: 'Source standard id', type: 'text', section: SECTION_CONTENT, advanced: true, help: 'Set automatically when filled from a standard.' },
  ],
  modal: {
    hero: ['title', 'statements'],
    columns: 2,
    sectionOrder: [SECTION_CONTENT, SECTION_STYLE, SECTION_COLORS, SECTION_FONTS],
  },
  gradeBands: {
    K2: { textFont: 'Fredoka', bulletStyle: 'star' },
    G35: { textFont: 'Nunito', bulletStyle: 'check' },
    G68: { textFont: 'Lexend', bulletStyle: 'check' },
    G912: { textFont: 'Arial', bulletStyle: 'dot' },
  },
  contentSource,
  async generate(params) {
    const W = 820;
    const pad = 32;
    const bannerH = 96;
    const innerPad = 28;
    const bulletBox = 44;
    const rowGap = 20;

    const title = rawStr(params.title, '').trim();
    const styleRaw = str(params.bulletStyle, 'check');
    const bulletStyle = styleRaw === 'star' || styleRaw === 'dot' ? styleRaw : 'check';
    const textColor = str(params.textColor, INK);
    const textFont = str(params.textFont, DEFAULT_FONT);
    const partFont = (key: string): string => {
      const v = params[key];
      return typeof v === 'string' && v.trim() !== '' ? v : textFont;
    };
    const titleFont = partFont('titleFont');
    const statementFont = partFont('statementFont');

    const bannerFill = (params.bannerFill ?? '#2563eb') as FillValue;
    const cardFill = (params.cardFill ?? '#ffffff') as FillValue;
    const accent = solidFromFill(bannerFill, '#2563eb');

    let statements = jsonArray<unknown>(params.statements, SAMPLE_STATEMENTS)
      .map((s) => rawStr(s, '').trim())
      .filter(Boolean)
      .slice(0, MAX_STATEMENTS);
    if (statements.length === 0) statements = [...SAMPLE_STATEMENTS];

    const cardX = pad;
    const cardTop = bannerH + 24;
    const cardW = W - pad * 2;
    const textLeft = cardX + innerPad + bulletBox + 16;
    const textW = Math.max(40, cardW - innerPad * 2 - bulletBox - 16);

    // Pass 1 — build/measure each statement's Textbox, accumulate row heights.
    interface Row {
      box: Textbox;
      rowH: number;
    }
    const rows: Row[] = statements.map((s) => {
      const box = new Textbox(s, {
        width: textW,
        fontSize: 26,
        fontFamily: statementFont,
        fill: textColor,
        textAlign: 'left',
        lineHeight: 1.15,
        originX: 'left',
        originY: 'center',
      });
      return { box, rowH: Math.max(bulletBox, box.height ?? 26) + rowGap };
    });
    const listH = rows.reduce((s, r) => s + r.rowH, 0);
    const cardH = listH + innerPad * 2 - rowGap;
    const H = cardTop + cardH + pad;

    const objects: FabricObject[] = [];
    const bannerRects: FabricObject[] = [];
    const cardRects: FabricObject[] = [];

    objects.push(new Rect({ left: 0, top: 0, width: W, height: H, fill: 'transparent', strokeWidth: 0 }));

    // Card (drawn before the banner so the banner's rounded bottom reads clean).
    const card = new Rect({ left: cardX, top: cardTop, width: cardW, height: cardH, rx: 22, ry: 22 });
    cardRects.push(card);
    objects.push(card);

    // Banner + title.
    if (title) {
      const banner = new Rect({ left: pad, top: pad, width: W - pad * 2, height: bannerH - pad, rx: 20, ry: 20 });
      bannerRects.push(banner);
      objects.push(banner);
      objects.push(
        new Textbox(title, {
          left: W / 2,
          top: pad + (bannerH - pad) / 2,
          width: W - pad * 2 - 32,
          fontSize: 44,
          fontFamily: titleFont,
          fontWeight: 'bold',
          fill: readableInkOn(accent, INK),
          textAlign: 'center',
          originX: 'center',
          originY: 'center',
        }),
      );
    }

    // Pass 2 — rows: drawn bullet + statement text, vertically centered.
    let y = cardTop + innerPad;
    for (const r of rows) {
      const cy = y + (r.rowH - rowGap) / 2;
      const bx = cardX + innerPad + bulletBox / 2;
      objects.push(...bulletShapes(bulletStyle, bx, cy, bulletBox * 0.82, accent));
      r.box.set({ left: textLeft, top: cy });
      objects.push(r.box);
      y += r.rowH;
    }

    await Promise.all([
      ...bannerRects.map((o) => applyFill(o, bannerFill, '#2563eb')),
      ...cardRects.map((o) => applyFill(o, cardFill, '#ffffff')),
    ]);

    const group = new Group(objects);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, iCanStatements, params);
  },
};

registerRecipe(iCanStatements);
